import React, { useState, useRef, useEffect } from "react";
import { Star, MoreVertical, Clock, MapPin, ExternalLink } from 'lucide-react';
import PhotoGrid from './Component/PhotoGrid';
import { useAuth } from '../../providers/auth';
import useModal from "../../hooks/useModal";
import CreatePostForm from "./Component/CreatePostForm";
import CreateEventForm from "./Component/CreateEventForm";
import DialogWrapper from "../custom-ui/dialog/Dialog";
import CrossSVG from "../custom-ui/icons/CrossSVG";
import { updateEventStatus } from "../../data/event";
import InstantTeamsSpinner from "../custom-ui/animations/spinner/InstantTeamsSpinner";
import LoadingSpinner from "../dashboard/AdminMetricDashboard/LoadingIndicator/LoadingSpinner";
import { sanitizeMarkup } from "../../util/sanitize/sanitize-html";
import ToggleButton from "./Component/Toogle";
import GreenUnderlineHeading from "../custom-ui/headings/heading";
import WistiaPlayer from "./WistiaPlayer";
import EventbriteCheckoutModal from "./Component/EventbriteCheckoutModal";


const EventCard = ({ data, onOpenLightbox, pastEvent = false }: any) => {
    const imageList = Array.isArray(data?.image) ? data.image : [];
    const isMultiImagePost = imageList.length;
    const [featured, setFeatured] = useState(data?.featured || "non-featured");
    const [status, setStatus] = useState(data?.status || "Active");
    const primaryImage = imageList[0];
    const { user } = useAuth();
    const { isShowing, toggle }: any = useModal();
    const { isShowing: openActiveModal, toggle: toggleOpenActiveModal }: any = useModal();
    const { isShowing: openFeaturedModal, toggle: toggleOpenFeaturedModal }: any = useModal();
    const { isShowing: openCheckoutModal, toggle: toggleCheckoutModal }: any = useModal();
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef(null);
    const id = data?.id;
    const isEventbriteEvent = data?.source === "eventbrite" && Boolean(data?.eventbriteId);



    const editModalRef = useRef(null);
    const activeModalRef = useRef(null);
    const featuredModalRef = useRef(null);

    useEffect(() => {
        function handleOutsideClick(e: MouseEvent) {
            const target = e.target as Node;

            if (isShowing && editModalRef.current && !editModalRef.current.contains(target)) {
                toggle();
            }

            if (openActiveModal && activeModalRef.current && !activeModalRef.current.contains(target)) {
                toggleOpenActiveModal();
            }

            if (openFeaturedModal && featuredModalRef.current && !featuredModalRef.current.contains(target)) {
                toggleOpenFeaturedModal();
            }
        }

        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [isShowing, openActiveModal, openFeaturedModal]);



    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowMenu(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);



    const handleImageClick = (index = 0) => {
        if (isMultiImagePost) {
            onOpenLightbox({
                images: imageList,
                title: data.title,
                description: data.description,
                author: data.updated_by,
                lastUpdated: data.lastUpdated,
                eventUrl: data?.eventUrl || "",
                wistiaMediaId: data?.wistiaMediaId || "",
                post_type: data.post_type || "",
                pastEvent: pastEvent,
                time: data?.time || "",
                location: data?.location || "",
                date: {
                    date: data.date.days,
                    monthYear: data.date.monthYear

                },
                link: data?.link,
                source: data?.source || "",
                eventbriteId: data?.eventbriteId || "",

            }, index);
        }
    };

    const markArchived = () => {
        updateEventStatus(id, {
            status: data?.status === "archived" ? "active" : "archived",
        })
            .finally(() => toggleOpenActiveModal());
    };

    const markNonFeatured = () => {
        const newIsFeatured = data?.featured === "featured" ? false : true;
        const newFeaturedLabel = newIsFeatured ? "featured" : "non-featured";
        updateEventStatus(id, {
            isFeatuered: newIsFeatured,
            featured: newFeaturedLabel,
        })
            .finally(() => toggleOpenFeaturedModal());
    };

    const handleRegisterClick = () => {
        if (isEventbriteEvent) {
            toggleCheckoutModal();
            return;
        }

        window.open(data.link, "_blank");
    };


    return (
        <>
            <div className="bg-gray-100 rounded-lg w-full flex justify-center items-start">
                <div className="bg-white rounded-lg shadow-sm p-6 w-full max-w-[] font-sans mx-auto border border-gray-100">
                    <div className="flex justify-between items-start ">
                        <h1 className="text-[22px] font-bold text-gray-900 leading-tight">
                            {data.title}
                        </h1>
                        <div className="flex items-center gap-3 pl-4">

                            {data.isFeatured && <div className={`flex items-center ${pastEvent ? "bg-gray-300 text-gray-600" : "bg-[#FDB022]"} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                                <Star size={12} className="fill-current mr-1" strokeWidth={3} />
                                Featured
                            </div>}

                            {data.source === "eventbrite" && <div className="text-xs font-bold px-3 py-1 rounded-full bg-[#EEF2FF] text-[#3730A3]">
                                Eventbrite
                            </div>}

                            {user?.claims.type === "A" && data.status === "archived" && <div className={`text-xs font-bold px-3 py-1 rounded-full ${data.status === 'active' && !pastEvent
                                ? 'bg-[#A6E7C7] text-[#045F39]'
                                : 'bg-blue-200 text-gray-600'
                                }`}>
                                Archived
                            </div>}

                            {user?.claims.type === "A" && !pastEvent && <div className={`text-xs font-bold px-3 py-1 rounded-full ${data.status === 'active' && !pastEvent
                                ? 'bg-[#A6E7C7] text-[#045F39]'
                                : 'bg-blue-200 text-gray-600'
                                }`}>
                                Active
                            </div>}
                            {user?.claims.type === "A" && (
                                <div className="relative">
                                    <button
                                        onClick={() => setShowMenu((prev: any) => !prev)}
                                        className="text-gray-500 hover:bg-gray-100 rounded-full p-1 transition-colors"
                                    >
                                        <MoreVertical size={20} />
                                    </button>

                                    {showMenu && (
                                        <div
                                            ref={menuRef}
                                            className="absolute right-0 top-7 w-60 bg-white shadow-lg rounded-lg p-5 z-50 animate-scaleIn"
                                        >
                                            <div className="flex flex-col text-gray-700 space-y-5">

                                                <button onClick={() => toggle()} className="text-[16px] font-medium hover:text-black text-left">
                                                    Edit
                                                </button>

                                                <button onClick={() => toggleOpenActiveModal()} className="text-[16px] font-medium hover:text-black text-left">
                                                    {data?.status === "archived" ? "Mark as Active" : "Mark as Archived"}
                                                </button>

                                                <button onClick={() => toggleOpenFeaturedModal()} className="text-[16px] font-medium hover:text-black text-left">
                                                    {data?.featured === "non-featured" ? " Mark as Featured" : "Remove from Featured"}

                                                </button>

                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>
                        {/* Edit section end */}
                    </div>


                    {user?.claims.type === "A" && data.updated_by && (
                        <div className="text-[13px] text-gray-500 mb-4">
                            <span>Last updated by <span className="font-semibold text-gray-800">{data.updated_by}</span></span>
                        </div>
                    )}

                    {/* Description */}
                    <div
                        className="prose prose-sm text-[#4B5563] mb-6 max-w-none"
                        dangerouslySetInnerHTML={sanitizeMarkup(data.description)}
                    />

                    {/* Date and Location Details Box */}
                    {data?.post_type === "event" && <div className="flex justify-center flex-col rounded-2xl p-5 bg-[#F3F4F6] overflow-hidden mb-6 h-auto">
                        <div className="flex h-20">
                            <div className={`${pastEvent ? "bg-gray-300 text-gray-600" : "bg-[#A9D9B7] text-[#064E3B]"} w-[130px] flex flex-col rounded-lg justify-center items-center`}>
                                <span className="text-xs font-extrabold tracking-wider uppercase mb-0">{data?.date?.monthYear}</span>
                                <span className="text-3xl font-extrabold leading-none">{data?.date?.days}</span>
                            </div>

                            <div className="bg-[#F3F4F6] flex-1 flex flex-col justify-center pl-6 space-y-2">
                                {data.time && (
                                    <div className="flex items-center text-[#374151] text-sm font-medium">
                                        <Clock size={18} className="mr-3 text-gray-400" strokeWidth={2} />
                                        <span>{data.time}</span>
                                    </div>
                                )}
                                {data.location && (
                                    <div className="flex items-center text-[#374151] text-sm font-medium">
                                        <MapPin size={18} className="mr-3 text-gray-400" strokeWidth={2} />
                                        <span>{data.location}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <button
                            disabled={pastEvent}
                            onClick={handleRegisterClick}
                            className={` w-full  ${pastEvent ? "bg-gray-300 " : "bg-instant-teams-blue-D1 hover:bg-gray-900 "}  transition-colors text-white  font-semibold py-3.5 px-4 rounded-xl flex items-center justify-center mt-5 text-sm`}>

                            Register
                            {!isEventbriteEvent && <ExternalLink size={18} className="ml-2.5" strokeWidth={2} />}
                        </button>
                    </div>}

                    {data?.wistiaWebinarId && primaryImage && (
                        <div className="relative w-full h-[280px] rounded-md overflow-hidden mb-6">
                            <img
                                src={primaryImage}
                                alt={data.title}
                                className="w-full h-full object-cover object-center"
                            />
                        </div>
                    )}


                    {data?.wistiaMediaId && (
                        <WistiaPlayer
                            mediaId={data.wistiaMediaId}
                            title={data.title}
                            className="mb-6"
                        />
                    )}

                    {!data?.wistiaWebinarId && (
                        isMultiImagePost ? (
                            <PhotoGrid images={imageList} onImageClick={handleImageClick} />
                        ) : (
                            <>
                                {primaryImage && !data?.wistiaMediaId && (
                                    <div className="relative w-full h-[280px] rounded-md overflow-hidden group mt-auto">
                                        <img
                                            src={primaryImage}
                                            alt={data.title}
                                            className="w-full h-full object-cover object-center"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
                                        {data.type === 'event' && (
                                            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                                                <button className="w-2 h-2 bg-white rounded-full"></button>
                                                <button className="w-2 h-2 bg-white/40 rounded-full"></button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )
                    )}

                </div>

            </div>



            {
                openCheckoutModal && isEventbriteEvent && (
                    <EventbriteCheckoutModal
                        isOpen={openCheckoutModal}
                        onClose={toggleCheckoutModal}
                        eventbriteId={data.eventbriteId}
                    />
                )
            }

            {
                isShowing && <DialogWrapper
                    title={data.post_type === "post" ? "Edit Post" : ""}
                    size={data.post_type === "post" ? "w-[calc(100%-1.5rem)] max-w-[640px]" : "w-[50%]"}
                    onClose={toggle}
                    displayCloseIcon={true}
                    contentClassName={data.post_type === "post" ? "!px-8 !py-8 sm:!px-10 sm:!py-10" : ""}
                >
                    <div ref={editModalRef}>
                        {data.post_type === "post" ? <CreatePostForm toggleModal={toggle} id={id} /> : (
                            <>
                                <div className='flex mt-[-23px] mr-[-20px] justify-end'>
                                    <button
                                        className="w-[48px] h-[48px] rounded-full bg-[#F8F9FA] text-[#183852] focus:outline-none hover:bg-slate-200 flex flex-row justify-center items-center"
                                        onClick={toggle}
                                    >
                                        <CrossSVG />
                                    </button>
                                </div>
                                <CreateEventForm toggleModal={toggle} id={id} />
                            </>
                        )}
                    </div>

                </DialogWrapper>

            }

            {
                openActiveModal && (
                    <DialogWrapper
                        title=""
                        size="w-[60%]"
                        onClose={toggleOpenActiveModal}
                        displayCloseIcon={true}
                    >
                        <div ref={activeModalRef}>
                            {/* Close Button */}
                            <div className="flex mt-[-23px] mr-[-20px] justify-end">
                                <button
                                    className="w-[48px] h-[48px] rounded-full bg-[#F8F9FA] text-[#183852] hover:bg-slate-200 flex justify-center items-center"
                                    onClick={toggleOpenActiveModal}
                                >
                                    <CrossSVG />
                                </button>
                            </div>

                            <h2 className="text-[#1a3b5d] text-xl font-bold  mb-4">{`Are You Sure You Want to Mark This As${data?.status === "archived" ? " Active" : " Archived"}?`}</h2>
                            <div className="h-0.5 bg-green-700 mb-6"></div>
                            <ToggleButton
                                isOn={status === "active"}
                                buttonText={status === "active" ? "Active" : "Archived"}
                                handleClick={() => {
                                    setStatus((prev: any) =>
                                        prev === "active" ? "archived" : "active"
                                    );
                                    markArchived();
                                }

                                }

                            />
                        </div>
                    </DialogWrapper>
                )
            }

            {
                openFeaturedModal && (
                    <DialogWrapper
                        title=""
                        size="w-[60%]"
                        onClose={toggleOpenFeaturedModal}
                        displayCloseIcon={true}
                    >
                        <div ref={featuredModalRef}>
                            {/* Close Button */}
                            <div className="flex mt-[-23px] mr-[-20px] justify-end">
                                <button
                                    className="w-[48px] h-[48px] rounded-full bg-[#F8F9FA] text-[#183852] hover:bg-slate-200 flex justify-center items-center"
                                    onClick={toggleOpenFeaturedModal}
                                >
                                    <CrossSVG />
                                </button>
                            </div>
                            <h2 className="text-[#1a3b5d] text-xl font-bold mb-4">{` ${data?.featured === "non-featured" ? " Mark this post as featured" : " Remove this post from featured"}?`}</h2>
                            <div className="h-0.5 bg-green-700 mb-6"></div>

                            {/* Standalone Toggle */}
                            <ToggleButton
                                isOn={featured === "featured"}
                                buttonText={featured === "featured" ? "Featured" : "Non - Featured"}
                                handleClick={() => {
                                    setFeatured((prev: any) =>
                                        prev === "featured" ? "non-featured" : "featured"
                                    );
                                    markNonFeatured();
                                }

                                }

                            />
                        </div>
                    </DialogWrapper>
                )
            }

        </>

    );
};

export default EventCard;